import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { tokens } from "@/tokens";

interface StandardConfirmationModalProps extends StandardModalProps {
  maxWidth: "xs" | "sm" | "md" | "lg" | "xl";
  noPadding?: boolean;
  onConfirm: () => void;
  icon?: React.ReactNode;
  title: string;
  description: string;
  confirmButtonColor?: "error" | "success";
  confirmButtonTitle?: string;
  confirmButtonLoader?: boolean;
}

const StandardConfirmationModal = ({
  open,
  onClose,
  maxWidth,
  onConfirm,
  icon: _icon,
  title,
  description,
  confirmButtonColor = "error",
  confirmButtonTitle = "Confirm",
  confirmButtonLoader,
}: StandardConfirmationModalProps) => {
  const isDanger = confirmButtonColor === "error";
  const btnBg = isDanger ? tokens.status.danger : tokens.status.success;
  const btnHoverBg = isDanger ? "#dc2626" : "#059669";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: tokens.radius.lg,
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <Box sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: `1px solid ${tokens.border.default}` }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography sx={{ fontSize: tokens.fontSize.xl, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary, lineHeight: 1.3 }}>
              {title}
            </Typography>
            <Typography sx={{ fontSize: tokens.fontSize.sm2, color: tokens.text.muted, mt: 0.5 }}>
              {description}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{ color: tokens.text.muted, "&:hover": { color: tokens.text.primary } }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Footer */}
      <Box sx={{
        px: 3,
        py: 2,
        display: "flex",
        justifyContent: "flex-end",
        gap: 1.5,
      }}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={confirmButtonLoader}
          sx={{
            borderColor: tokens.border.default,
            color: tokens.text.secondary,
            fontWeight: tokens.fontWeight.semibold,
            fontSize: tokens.fontSize.sm2,
            borderRadius: tokens.radius.md,
            textTransform: "none",
            px: 3,
            "&:hover": { borderColor: tokens.text.muted, bgcolor: tokens.bg.hover },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={confirmButtonLoader}
          sx={{
            fontWeight: tokens.fontWeight.semibold,
            fontSize: tokens.fontSize.sm2,
            borderRadius: tokens.radius.md,
            textTransform: "none",
            px: 3,
            bgcolor: btnBg,
            color: "#fff",
            boxShadow: "none",
            "&:hover": { bgcolor: btnHoverBg, boxShadow: "none" },
          }}
        >
          {confirmButtonLoader && <CircularProgress size={16} sx={{ color: "white", mr: 1 }} />}
          {confirmButtonTitle}
        </Button>
      </Box>
    </Dialog>
  );
};

export default StandardConfirmationModal;
